<template>
  <DetailInfo class="job-application-info" :btnList="btnList">
    <PostApplicantReadonly
      v-if="activeInvitation"
      :data="activeInvitation"
      :candidateName="candidateBaseInfo.other_name"
      :disabled="isDisabled"
    />
    <InviteJobApplicationQuestionnaire
      ref="inviteDialog"
      :jobId="jobId"
      :candidateId="candidateId"
      @success="loadJobApplicationList"
    />
    <ReactiveJobApplication
      :show.sync="reactivateDialog.show"
      :questionnaire="activeInvitation"
      @save="onReactivateConfirm"
    />
  </DetailInfo>
</template>

<script>
import _ from 'lodash'
import { mapActions } from 'vuex'
import DetailInfo from './components/detailInfo.vue'
import PostApplicantReadonly from './components/postApplicantReadonly.vue'
import InviteJobApplicationQuestionnaire from './dialog/inviteJobApplicationQuestionnaire.vue'
import ReactiveJobApplication from './dialog/reactiveJobApplication.vue'
import { AsyncTask } from '@/utils/common'

export default {
  components: {
    DetailInfo,
    PostApplicantReadonly,
    InviteJobApplicationQuestionnaire,
    ReactiveJobApplication
  },
  props: {
    candidateBaseInfo: { type: Object, required: true },
    isDisabled: { type: Boolean, default: false }
  },
  data () {
    return {
      i18nPrefix: 'admin.application.candidate',
      loading: false,
      exportPdfStatus: {
        loading: false,
        task: null,
        finished: false
      },
      applicationList: [],
      activeIndex: 0,
      reactivateDialog: { show: false }
    }
  },
  computed: {
    candidateId () {
      return this.candidateBaseInfo.id
    },
    jobId () {
      return this.candidateBaseInfo.job && this.candidateBaseInfo.job.id
    },
    activeInvitation () {
      return this.applicationList[this.activeIndex] || null
    },
    activeFinishedInvitation () {
      return this.activeInvitation && this.activeInvitation.invitation_is_finished ? this.activeInvitation : null
    },
    unfinishedInvitation () {
      return _.find(this.applicationList, item => !item.invitation_is_finished)
    },
    btnList () {
      const vm = this
      return [
        {
          label: this.$t(`${this.i18nPrefix}.btn.inviteJobApplication`),
          onclick: this.onInviteClick,
          hidden: this.applicationList.length > 0,
          get disabled () { return vm.isDisabled }
        },
        {
          label: this.getInvitationStatusLabel(),
          hidden: !this.unfinishedInvitation,
          disabled: true
        },
        {
          label: this.$t(`${this.i18nPrefix}.btn.remindJobApplication`),
          onclick: this.onReminderClick,
          hidden: !this.unfinishedInvitation,
          get loading () { return vm.loading },
          get disabled () { return vm.isDisabled || !vm.unfinishedInvitation }
        },
        {
          label: this.$t(`${this.i18nPrefix}.btn.downloadJobApplicationPDF`),
          onclick: this.onExportClick,
          hidden: !this.activeFinishedInvitation,
          get loading () {
            const exportPdfStatus = vm.exportPdfStatus
            return exportPdfStatus?.task && !exportPdfStatus.finished
          },
          get disabled () { return vm.isDisabled || !vm.activeFinishedInvitation }
        },
        {
          label: this.$t(`${this.i18nPrefix}.btn.reactivateJobApplication`),
          onclick: this.onReactivateClick,
          hidden: !this.activeFinishedInvitation,
          get disabled () { return vm.isDisabled || !vm.activeFinishedInvitation }
        }
      ]
    }
  },
  created () {
    this.loadJobApplicationList()
  },
  methods: {
    ...mapActions('application', ['getApplicationCandidateSurveyInfo']),
    ...mapActions('jobApplicationManagement', [
      'updateInvitation',
      'reactivateJobApplicationInvitation',
      'createJobApplicationPdfExportTask',
      'getJobApplicationPdfExportTaskStatus'
    ]),
    async loadJobApplicationList () {
      if (!this.candidateId || !this.jobId) {
        return
      }
      this.loading = true
      try {
        const resp = await this.getApplicationCandidateSurveyInfo({
          candidate_id: this.candidateId,
          job_id: this.jobId,
          survey_type: 'job_application'
        })
        this.applicationList = _.filter(resp || [], { questionnaire_scope: 'JOB_APPLICATION' })
      } finally {
        this.loading = false
      }
    },
    onInviteClick () {
      this.$refs.inviteDialog.openDialog()
    },
    getInvitationStatusLabel () {
      const operationRecords = (this.unfinishedInvitation && this.unfinishedInvitation.operation_records) || []
      const reactivatedRecord = _.findLast(operationRecords, { operation_type: 'REACTIVATED' })
      const record = reactivatedRecord || _.findLast(operationRecords, { operation_type: 'INVITED' })
      const time = record ? record.operation_time : this.unfinishedInvitation && this.unfinishedInvitation.invitation_time
      const i18nKey = reactivatedRecord ? 'reactivatedJobApplication' : 'resendJobApplication'
      return this.$t(`${this.i18nPrefix}.btn.${i18nKey}`, { time: time ? time.replace(/-/g, '/') : '' })
    },
    async onReminderClick () {
      if (!this.unfinishedInvitation || this.loading) {
        return
      }
      this.loading = true
      try {
        await this.updateInvitation({ id: this.unfinishedInvitation.invitation_id, is_retry: true })
        this.$message.success(this.$t(`${this.i18nPrefix}.message.remindJobApplicationSuccess`))
        await this.loadJobApplicationList()
      } finally {
        this.loading = false
      }
    },
    onExportClick () {
      if (!this.activeFinishedInvitation) {
        return
      }
      const exportPdfStatus = this.exportPdfStatus
      if (exportPdfStatus.loading) {
        return
      }
      const invitationId = this.activeFinishedInvitation.invitation_id
      exportPdfStatus.loading = true
      exportPdfStatus.finished = false
      exportPdfStatus.task = new AsyncTask(() => this.createJobApplicationPdfExportTask({
        invitation_id: invitationId,
        language_code: this.$i18n.locale
      }), (taskId) => this.getJobApplicationPdfExportTaskStatus({
        invitation_id: invitationId,
        task_id: taskId
      }), (taskId) => {
        window.location.href = this.$DjangoBaseUrl(`/django_rest/job-application-management/invitation-record/${invitationId}/export-pdf/?task_id=${taskId}&download=1`)
        exportPdfStatus.loading = false
        exportPdfStatus.finished = false
        exportPdfStatus.task = null
      })
    },
    onReactivateClick () {
      this.reactivateDialog.show = true
    },
    async onReactivateConfirm ({ reason, emailTemplateId }, callback) {
      try {
        await this.reactivateJobApplicationInvitation({
          id: this.activeFinishedInvitation.invitation_id,
          reason: reason || '',
          email_template_id: emailTemplateId
        })
        this.$message.success(this.$t(`${this.i18nPrefix}.message.reactivateSuccess`))
        await this.loadJobApplicationList()
      } finally {
        callback()
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.job-application-info {
  .empty-text {
    padding: 24px;
    color: #8b8b8b;
  }
}
</style>
